import type React from 'react';
import { useCallback } from 'react';
import { hexToVec3 } from '../../utils';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { TextureShader } from '../../texture-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

// 1. SnowfallShader Model
export type SnowfallShaderConfig = BaseShaderConfig & {
  name: 'snowfall';
  color: string;
  backgroundColor: string;
  speed: number;
  density: number;
};

export class SnowfallShader extends TextureShader {
  name = 'snowfall' as const;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;
  color!: string;
  backgroundColor!: string;
  speed!: number;
  density!: number;

  constructor(props: Partial<SnowfallShader> = {}) {
    super();

    // Set defaults
    this.name = 'snowfall';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;
    this.color = '#ffffff';
    this.backgroundColor = '#000000';
    this.speed = 1.0;
    this.density = 0.5;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
            precision mediump float;
            ${this.getTextureUniforms()}
            uniform vec3 u_color;
            uniform vec3 u_background_color;
            uniform float u_speed;
            uniform float u_density;

            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            void main() {
                ${this.getTextureUVPreamble()}

                // If we're outside the reference bounds, show background
                if (isOutOfBounds) {
                    gl_FragColor = vec4(u_background_color, 1.0);
                    return;
                }

                float t = u_time * u_speed;
                vec3 color = u_background_color;

                // Create multiple layers of snow
                for(int layer = 0; layer < 3; layer++) {
                    float layerFloat = float(layer);
                    float layerSpeed = 1.0 + layerFloat * 0.3;
                    float layerSize = 0.8 + layerFloat * 0.4;

                    // Offset each layer
                    vec2 layerUV = uv + vec2(layerFloat * 0.1, 0.0);

                    // Create grid for snowflakes
                    vec2 grid = layerUV * u_density * (10.0 + layerFloat * 5.0);
                    vec2 gridId = floor(grid);
                    vec2 gridUV = fract(grid);

                    // Animate falling
                    gridUV.y = fract(gridUV.y + t * layerSpeed * 0.1);

                    // Random position within cell
                    vec2 randomOffset = vec2(
                        random(gridId + layerFloat),
                        random(gridId + layerFloat + 100.0)
                    ) * 0.8 + 0.1;

                    // Distance to snowflake center
                    float dist = length(gridUV - randomOffset);

                    // Create snowflake
                    float snowflake = 1.0 - smoothstep(0.02 * layerSize, 0.05 * layerSize, dist);

                    // Add some randomness to snowflake visibility
                    float visibility = step(0.3, random(gridId + layerFloat + 200.0));
                    snowflake *= visibility;

                    // Layer opacity (front layers more opaque)
                    float layerOpacity = 1.0 - layerFloat * 0.3;

                    // Mix with existing color
                    color = mix(color, u_color, snowflake * layerOpacity);
                }

                gl_FragColor = vec4(color, 1.0);
            }
        `;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'speed',
        label: 'Fall Speed',
        description: 'How fast the snow falls',
        min: 0.1,
        max: 5,
        step: 0.1,
        defaultValue: 1.0,
        colspan: 1,
      },
      {
        type: 'range',
        name: 'density',
        label: 'Snow Density',
        description: 'Amount of snowflakes',
        min: 0.1,
        max: 2,
        step: 0.1,
        defaultValue: 0.5,
        colspan: 2, // Full width for main effect control
      },
      {
        type: 'color',
        name: 'color',
        label: 'Snow Color',
        description: 'Color of the snowflakes',
        defaultValue: '#ffffff',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color',
        defaultValue: '#000000',
        colspan: 1,
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.TEXTURE,
      category: ShaderCategory.SIMULATIONS,
      description:
        'Realistic snowfall simulation with customizable density and speed',
      tags: ['snow', 'snowfall', 'winter', 'simulation', 'particles'],
    };
  }
}

// 2. SnowfallPreview Component
export const SnowfallPreview: React.FC<{ model: SnowfallShader }> = ({
  model,
}) => {
  const getUniformNames = useCallback(
    () => ['u_color', 'u_background_color', 'u_speed', 'u_density'],
    []
  );

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: SnowfallShader
    ) => {
      // Update shader-specific uniforms
      gl.uniform1f(locations.u_speed, model.speed);
      gl.uniform1f(locations.u_density, model.density);

      // Update color uniforms
      const colorVec3 = hexToVec3(model.color);
      const backgroundColorVec3 = hexToVec3(model.backgroundColor);

      gl.uniform3fv(locations.u_color, colorVec3);
      gl.uniform3fv(locations.u_background_color, backgroundColorVec3);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return <canvas ref={canvasRef} width={model.width} height={model.height} />;
};
